import { useState } from 'react';
import { motion } from 'framer-motion';
import { LogIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { login } from '@/lib/auth';
import type { ApiError } from '@/lib/api';

export function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await login(email, password);
      window.location.href = '/';
    } catch (e) {
      const ae = e as ApiError;
      setErr(t(`error.${ae.code}`, { defaultValue: ae.message }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-surface-soft">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        className="w-full max-w-[420px]"
      >
        <div className="text-center mb-8 flex flex-col items-center">
          <img
            src="/logica-erp-wordmark.png"
            alt="Logica ERP"
            className="h-12 w-auto select-none dark:hidden"
            draggable={false}
          />
          <img
            src="/logica-erp-wordmark-white.png"
            alt="Logica ERP"
            className="h-12 w-auto select-none hidden dark:block"
            draggable={false}
          />
          <p className="mt-5 text-body-sm text-slate">
            {t('login.subtitle', 'Sign in to continue')}
          </p>
        </div>

        <div className="bg-canvas border border-hairline rounded-xl p-6">
          <form onSubmit={submit} className="space-y-4">
            <Field label={t('login.email', 'Email')} htmlFor="email">
              <Input
                id="email" type="email" autoComplete="email" required
                placeholder="nama@perusahaan.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label={t('login.password', 'Password')} htmlFor="password">
              <Input
                id="password" type="password" autoComplete="current-password" required minLength={8}
                placeholder="••••••••"
                value={password} onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {err && (
              <div className="rounded-md bg-brand-error/10 text-brand-error text-caption px-3 py-2">
                {err}
              </div>
            )}
            <Button type="submit" size="lg" loading={busy} className="w-full">
              {!busy && <LogIn className="size-4" />}
              {busy ? t('login.signing_in', 'Signing in…') : t('login.sign_in', 'Sign in')}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
